import {
    getAllAssignees,
    sprintWeek,
    labelStatus,
    projectTaskStatus,
    projectMilestones,
    groupAssigneeNames,
    getTime
} from "./TaskData"
const { getISOWeek } = require('date-fns');

/**
 * Superclass for group org project tasks, extended by TaskIssue and TaskPullRequest
 */
class Task {

    /**
     * Class constructor for the group org project task superclass
     * @param {number} number - ID of the task Issue or Pull Request for the associated repository
     * @param {string} title - title given to the task Issue or Pull Request
     * @param {string} body - body of the task Issue or Pull Request
     * @param {string} createdAt - date string representing the date and time the task Issue or Pull Request is created
     * @param {string} closedAt - date string representing the date and time the task Issue or Pull Request is closed
     * @param {string} kanbanStatus - Kanban board status of the task Issue or Pull Request: In Progress, Done, etc.
     * @param {string} url - the url of the task Issue or Pull Request
     * @param {[Object]} comments - array of comment objects made to the task Issue containing commenter and body data
     */
    constructor(number, title, body, createdAt, closedAt, kanbanStatus, url, comments) {
        this.number = number;
        this.title = title;
        this.body = body;
        this.kanbanStatus = projectTaskStatus[kanbanStatus];
        this.url = url;
        this.comments = comments

        this.createdAt = createdAt.split('T')[0];
        this.closedAt = closedAt ? closedAt.split('T')[0] : closedAt;
        this.isClosed = this.closedAt !== null;

        this.sprints = [sprintWeek[getISOWeek(new Date(this.createdAt))],
                        this.isClosed ? sprintWeek[getISOWeek(new Date(this.closedAt))] : null];
    }
}

/**
 * Subclass for group org project task Pull Requests, extending the Task superclass
 */
class TaskIssue extends Task {

    /**
     * Class constructor for the group org project task Issue subclass of Task
     * @param {number} number - ID of the task Issue for the associated repository
     * @param {string} title - title given to the task Issue
     * @param {string} body - body of the task Issue
     * @param {string} createdAt - date string representing the date and time the task Issue is created
     * @param {string} closedAt - date string representing the date and time the task Issue is closed
     * @param {string} status - Kanban board status of the task Issue: In Progress, Done, etc.
     * @param {string} url - the url of the task Issue
     * @param {[Object]} comments - array of comment objects made to the task Issue containing commenter and body data
     * @param {string} assignee - - username belonging to the group member assigned to the Issue this instance is for
     * @param {[Object]} peers - array of usernames belonging to all other the group members assigned to the Issue
     * @param {string} milestone - representing the milestone ID associated with the Issue
     * @param {[Object]} labels - array of labels applied to Issue representing task status and type in report format
     */
    constructor(number, title, body, createdAt, closedAt, status, url, comments, assignee, peers, milestone, labels) {
        super(number, title, body, createdAt, closedAt, status, url, comments);
        this.milestone = projectMilestones[milestone];
        this.assignee = groupAssigneeNames[assignee].toUpperCase();

        this.peers = peers;
        if (peers) {
            for (let peer in peers) {
                peers[peer] = groupAssigneeNames[peers[peer]].toUpperCase();
            }
            peers = peers.filter(peer => peer !== this.assignee);
            peers.sort((a, b) => a.split(' ').pop().localeCompare(b.split(' ').pop()));

            this.peers = '';
            for (let peer in peers) {
                if (this.peers.length > 0) {
                    this.peers = this.peers.concat(' and ');
                }
                this.peers = this.peers.concat(peers[peer]);
            }
        }

        this.labelWhat = [];
        this.labelStatus = [];
        for (let label in labels) {
            let isStatusLabel = false;

            for (let status in labelStatus) {
                if (labels[label] === labelStatus[status]) {
                    this.labelStatus.push(labels[label].toUpperCase());
                    isStatusLabel = true;
                    break;
                }
            }

            if (!isStatusLabel) {
                this.labelWhat.push(labels[label].toUpperCase());
            }
        }

        let timeSum = 0.0;
        for (let comment in comments) {
            if (comments[comment]['author']['login'].toLowerCase() !== 'codecov' &&
                this.assignee === groupAssigneeNames[comments[comment]['author']['login']].toUpperCase()) {
                timeSum += getTime(comments[comment]['body']);
            }
        }
        if (timeSum === 0.0 && this.assignee === Object.values(groupAssigneeNames)[0].toUpperCase()) {
            timeSum += getTime(this.body);
        }
        this.time = `(${timeSum === 0.0 ? '?' : timeSum} hours)`;
        this.pullRequests = [];
    }

    /**
     * Add PullRequest instance for Pull Request associated with task Issue
     * @param {TaskPullRequest} pullRequest PullRequest instance
     */
    addPullRequest(pullRequest) {
        this.pullRequests.push(pullRequest);
    }
}

/**
 * Subclass for group org project task Pull Requests, extending the Task superclass
 */
class TaskPullRequest extends Task {

    /**
     * Class constructor for the group org project task Pull Request subclass of Task
     * @param {number} number - ID of the task Pull Request for the associated repository
     * @param {string} title - title given to the task Pull Request
     * @param {string} body - body of the task Pull Request
     * @param {string} createdAt - date string representing the date and time the task Pull Request is created
     * @param {string} closedAt - date string representing the date and time the task Pull Request is closed
     * @param {string} kanbanStatus - Kanban board status of the task Pull Request: In Review, Done, etc.
     * @param {string} url - the url of the task Pull Request
     * @param {[Object]} comments - array of comment objects made to the task Issue containing commenter and body data
     * @param {[string]} assignees - array of usernames belonging to the group members assigned to the Pull Request
     * @param {[string]} reviewers - array of usernames belonging to the group members reviewing the Pull Request
     * @param {[string]} issueUrls - array of strings representing the Issue URLs associated with the Pull Request
     */
    constructor(number, title, body, createdAt, closedAt, kanbanStatus, url, comments, assignees, reviewers, issueUrls) {
        super(number, title, body, createdAt, closedAt, kanbanStatus, url, comments);

        this.assignees = [];
        assignees = getAllAssignees(assignees);
        for (let assignee in assignees) {
            this.assignees.push(groupAssigneeNames[assignees[assignee]].toUpperCase());
        }

        this.reviewers = {};
        for (let reviewer in reviewers) {
            this.reviewers[reviewer] = {
                'name': groupAssigneeNames[reviewers[reviewer]].toUpperCase(),
                'reviewTime': 0.0
            };
        }

        for (let comment in comments) {
            for (let reviewer in Object.keys(this.reviewers)) {
                if (comments[comment]['author']['login'].toLowerCase() !== 'codecov' &&
                    this.reviewers[reviewer]['name'] === groupAssigneeNames[comments[comment]['author']['login']].toUpperCase()) {
                    this.reviewers[reviewer]['reviewTime'] += getTime(comments[comment]['body']);
                }
            }
        }

        for (let reviewer in reviewers) {
            this.reviewers[reviewer]['reviewTime'] = `(${this.reviewers[reviewer]['reviewTime'] === 0.0 ? '?' : 
                this.reviewers[reviewer]['reviewTime']} hours)`;
        }

        this.issueUrls = issueUrls;
    }
}

export { TaskIssue, TaskPullRequest };
